import React, { useState, useEffect, useRef } from "react";
import { Dropdown, Checkbox, Button } from "antd";
import { DownOutlined } from "@ant-design/icons";
import "./columnSelector.css";

/**
 * "Columns" dropdown for Table listing pages. Toggling a checkbox only
 * edits a local draft - nothing changes on screen or on the server until
 * Save is pressed, so a half-finished selection never persists by accident.
 *
 * `open`/`onOpenChange` are controlled by the parent so it can close this
 * dropdown whenever another menu (Export, a row's action popover, ...) opens,
 * and vice versa - keeping only one overlay on screen at a time.
 */
const ColumnSelector = ({
  columns = [],
  visibleKeys = [],
  onSave,
  alwaysVisibleKeys = [],
  buttonLabel = "Columns",
  open,
  onOpenChange,
  saving = false,
}) => {
  const [draftKeys, setDraftKeys] = useState(visibleKeys);
  const listRef = useRef(null);

  useEffect(() => {
    if (open) {
      setDraftKeys(visibleKeys);
      // Reopening should always start at the top of the list rather than
      // wherever the user last scrolled to - the list DOM persists across
      // opens (antd keeps the overlay mounted), so the browser remembers it.
      // A short delay covers the first open, where the overlay's portal may
      // not be mounted yet at the moment `open` flips true.
      const timer = setTimeout(() => {
        if (listRef.current) {
          listRef.current.scrollTop = 0;
        }
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [open, visibleKeys]);

  const selectableColumns = columns.filter((col) => {
    const colKey = col.key || col.dataIndex;
    return colKey && !alwaysVisibleKeys.includes(colKey);
  });

  const handleToggle = (colKey, checked) => {
    setDraftKeys((prev) =>
      checked ? [...prev, colKey] : prev.filter((k) => k !== colKey)
    );
  };

  const handleSave = () => {
    onSave(draftKeys);
    onOpenChange(false);
  };

  const menu = (
    <div className="column-selector-menu" onClick={(e) => e.stopPropagation()}>
      <div className="column-selector-title">Show columns</div>
      <div className="column-selector-list" ref={listRef}>
        {selectableColumns.map((col) => {
          const colKey = col.key || col.dataIndex;
          return (
            <div className="column-selector-item" key={colKey}>
              <Checkbox
                checked={draftKeys.includes(colKey)}
                onChange={(e) => handleToggle(colKey, e.target.checked)}
              >
                {col.title}
              </Checkbox>
            </div>
          );
        })}
      </div>
      <div className="column-selector-footer">
        <Button type="primary" loading={saving} onClick={handleSave}>
          Save
        </Button>
      </div>
    </div>
  );

  return (
    <Dropdown
      overlay={menu}
      trigger={["click"]}
      open={open}
      onOpenChange={(visible) => {
        if (!visible) {
          setDraftKeys(visibleKeys);
        }
        onOpenChange(visible);
      }}
    >
      <Button className="btn-secondary">
        {buttonLabel} <DownOutlined />
      </Button>
    </Dropdown>
  );
};

export default ColumnSelector;
